// ── Echo Currency leveling costs ──────────────────────────────────────────────
// Index i = cost to go from level i+1 → i+2  (so index 0 = Lv1→Lv2 = 10)
export const LEVEL_COSTS = [10, 20, 35, 55, 80, 110, 150, 200, 260, 340, 440, 560, 700, 880, 1100, 1380, 1720, 2150, 2700];
// LEVEL_COSTS[i] is the cost to reach level i+2 from level i+1.
// Total echoes to reach level N = sum of LEVEL_COSTS[0..N-2]
export function totalEchoesToLevel(targetLevel) {
  if (targetLevel <= 1) return 0;
  let total = 0;
  for (let i = 0; i < targetLevel - 1; i++) total += LEVEL_COSTS[i];
  return total;
}
export const MAX_CARD_LEVEL = 20;

// ── Rarity config ─────────────────────────────────────────────────────────────
export const RARITY = {
  common:    { label: "Common",    statMult: 1.0,  border: "#888888", abilityLv: [10, 20] },
  uncommon:  { label: "Uncommon",  statMult: 1.4,  border: "#44cc66", abilityLv: [8,  18] },
  rare:      { label: "Rare",      statMult: 1.9,  border: "#4488ee", abilityLv: [6,  15] },
  epic:      { label: "Epic",      statMult: 2.6,  border: "#aa55ff", abilityLv: [5,  13] },
  legendary: { label: "Legendary", statMult: 3.75, border: "#ffcc22", abilityLv: [3,  10] },
};

// Boost cost per rarity (Void Shards)
export const BOOST_COST = {
  common: 5, uncommon: 8, rare: 12, epic: 18, legendary: 25,
};

// ── Base stat-per-level tables (Common ×1.0, multiply by rarity.statMult) ─────
// Returns the per-level increment for a given level (1-indexed).
export function basePerLevel(statType, level) {
  const bands = STAT_BANDS[statType];
  if (!bands) return 0;
  if (level <= 5)  return bands[0];
  if (level <= 10) return bands[1];
  if (level <= 15) return bands[2];
  return bands[3];
}

export const STAT_BANDS = {
  resourceYield:  [2, 3, 4, 5],
  damage:         [2, 3, 4, 5],
  speed:          [1.5, 2, 3, 4],
  hp:             [8, 12, 16, 22],
  shield:         [8, 12, 16, 22],
  forgeSpeed:     [2, 3, 4, 5],
  exp:            [2, 3, 4, 5],
  echoCurrency:   [2, 3, 4, 5],
  fuelEfficiency: [1.5, 2, 3, 4],
};

// Cumulative stat at a given level for a card with the given statType and rarity
export function cardTotalStat(statType, rarity, level) {
  const mult = RARITY[rarity]?.statMult ?? 1;
  let total = 0;
  for (let lv = 1; lv <= level; lv++) {
    total += basePerLevel(statType, lv) * mult;
  }
  return Math.round(total * 10) / 10;
}

// ── Collection milestone bonuses ──────────────────────────────────────────────
// Each entry: { cards: N, category: "all"|"resource"|"combat"|"echo", mult: X }
export const COLLECTION_MILESTONES = [
  { cards:  5, category: "all",      mult: 1.02 },
  { cards: 10, category: "resource", mult: 1.03 },
  { cards: 15, category: "combat",   mult: 1.03 },
  { cards: 20, category: "all",      mult: 1.04 },
  { cards: 25, category: "echo",     mult: 1.04 },
  { cards: 30, category: "resource", mult: 1.05 },
  { cards: 35, category: "combat",   mult: 1.05 },
  { cards: 40, category: "all",      mult: 1.06 },
  { cards: 45, category: "echo",     mult: 1.06 },
  { cards: 50, category: "all",      mult: 1.10 },
];

// ── Echo drop sources (base values, scale with research) ─────────────────────
export const ECHO_SOURCES = {
  mineT1:      { min: 1,  max: 2,  cardChance: 0.005 },
  mineT2:      { min: 3,  max: 5,  cardChance: 0.015 },
  mineT3:      { min: 7,  max: 12, cardChance: 0.03  },
  combatStd:   { min: 2,  max: 4,  cardChance: 0.01  },
  combatElite: { min: 8,  max: 15, cardChance: 0.04  },
  smelt:       { min: 1,  max: 3,  cardChance: 0.003 },
  smeltBulk:   { min: 5,  max: 10, cardChance: 0.01  },
  rift:        { min: 20, max: 40, cardChance: 0.08  },
  voidRift:    { min: 60, max: 100,cardChance: 0.15  },
  sector:      { min: 15, max: 25, cardChance: 0.05  },
  debris:      { min: 1,  max: 1,  cardChance: 0.002 },
};

// ── Card catalogue — 50 cards ─────────────────────────────────────────────────
// stat: internal stat key (maps to effectiveStats)
// statType: key into STAT_BANDS for per-level calculation
// For cards with multiple stats, stat is an array.

export const ECHO_CARDS = [

  // ── MINING (10) ────────────────────────────────────────────────────────────
  {
    id: "copper_vein", name: "Copper Vein", category: "mining",
    sources: ["copper"],
    rarity: "common", icon: "⛏️",
    acquireDesc: "Mine 50 Copper nodes",
    stat: "copperYield", statType: "resourceYield",
    statLabel: "Copper yield",
    ability1: { lv: 10, desc: "Copper nodes have 6% chance to drop bonus Hull Debris on depletion" },
    ability2: { lv: 20, desc: "Copper nodes respawn 20% faster permanently" },
  },
  {
    id: "debris_hauler", name: "Debris Hauler", category: "mining",
    sources: ["hull_debris"],
    rarity: "common", icon: "🪨",
    acquireDesc: "Collect 200 Hull Debris total",
    stat: "debrisYield", statType: "resourceYield",
    statLabel: "Hull Debris yield",
    ability1: { lv: 10, desc: "Hull Debris nodes respawn 15% faster" },
    ability2: { lv: 20, desc: "4% chance each Hull Debris node also drops a free Metal Alloy" },
  },
  {
    id: "glass_cutter", name: "Glass Cutter", category: "mining",
    sources: ["glass_shard"],
    rarity: "common", icon: "🔷",
    acquireDesc: "Mine 100 Glass Shard nodes",
    stat: "glassYield", statType: "resourceYield",
    statLabel: "Glass Shard yield",
    ability1: { lv: 10, desc: "Glass Shard nodes have 5% chance to drop a free Ionized Lens on depletion" },
    ability2: { lv: 20, desc: "Laser mining Glass Shard nodes costs 10% less laser energy" },
  },
  {
    id: "silica_storm", name: "Silica Storm", category: "mining",
    sources: ["silica_dust"],
    rarity: "uncommon", icon: "🌪️",
    acquireDesc: "Mine 100 Silica Dust nodes",
    stat: "silicaYield", statType: "resourceYield",
    statLabel: "Silica Dust yield",
    ability1: { lv: 8, desc: "Silica Dust nodes have 8% chance to drop Nano-Glass directly" },
    ability2: { lv: 18, desc: "Silica Dust node yield is doubled on the first hit (once per node)" },
  },
  {
    id: "titan_bore", name: "Titan Bore", category: "mining",
    sources: ["titanium_ore"],
    rarity: "uncommon", icon: "🔩",
    acquireDesc: "Mine 100 Titanium Ore nodes",
    stat: "titaniumYield", statType: "resourceYield",
    statLabel: "Titanium Ore yield",
    ability1: { lv: 8, desc: "Titanium Ore nodes have 8% chance to drop Titanium Plates directly" },
    ability2: { lv: 18, desc: "Mining drones harvest Titanium Ore nodes 25% faster" },
  },
  {
    id: "isotope_tap", name: "Isotope Tap", category: "mining",
    sources: ["isotope8"],
    rarity: "rare", icon: "☢️",
    acquireDesc: "Mine 80 Isotope 8 nodes",
    stat: "isotopeYield", statType: "resourceYield",
    statLabel: "Isotope 8 yield",
    ability1: { lv: 6, desc: "Isotope 8 nodes emit pulse on depletion, dealing minor AoE damage to nearby enemies" },
    ability2: { lv: 15, desc: "Each Isotope 8 node mined generates +3 bonus Echo Currency" },
  },
  {
    id: "dark_extractor", name: "Dark Extractor", category: "mining",
    sources: ["dark_matter"],
    rarity: "rare", icon: "🕳️",
    acquireDesc: "Mine 50 Dark Matter nodes",
    stat: "darkMatterYield", statType: "resourceYield",
    statLabel: "Dark Matter yield",
    ability1: { lv: 6, desc: "Every 10th Dark Matter node mined drops a bonus Void Matter" },
    ability2: { lv: 15, desc: "Dark Matter nodes take 30% less time for drones to harvest" },
  },
  {
    id: "living_harvest", name: "Living Harvest", category: "mining",
    sources: ["living_metal"],
    rarity: "epic", icon: "🌿",
    acquireDesc: "Mine 50 Living Metal nodes",
    stat: "livingMetalYield", statType: "resourceYield",
    statLabel: "Living Metal yield",
    ability1: { lv: 5, desc: "Living Metal nodes slowly regenerate — revisit within 90s for bonus 15% harvest" },
    ability2: { lv: 13, desc: "Living Metal nodes have 10% chance to drop free Bio-Steel on depletion" },
  },
  {
    id: "solar_siphon", name: "Solar Siphon", category: "mining",
    sources: ["plasma_condensate"],
    rarity: "epic", icon: "☀️",
    acquireDesc: "Mine 40 Plasma Condensate nodes",
    stat: "plasmaYield", statType: "resourceYield",
    statLabel: "Plasma Condensate yield",
    ability1: { lv: 5, desc: "Plasma Condensate nodes occasionally erupt — tractor beam auto-collects ejected loot" },
    ability2: { lv: 13, desc: "Plasma Condensate nodes have 10% chance to drop free Star-Fire on depletion" },
  },
  {
    id: "void_cartographer", name: "Void Cartographer", category: "mining",
    sources: ["copper", "hull_debris", "glass_shard", "silica_dust", "titanium_ore", "isotope8", "dark_matter", "living_metal", "plasma_condensate"],
    rarity: "legendary", icon: "🗺️",
    acquireDesc: "Mine all 9 resource types at least once AND reach a T3 sector",
    stat: "allResourceYield", statType: "resourceYield",
    statLabel: "ALL resource yields",
    ability1: { lv: 3, desc: "All resource nodes within 2 sectors are permanently visible on the map" },
    ability2: { lv: 10, desc: "Every fully depleted node has 5% chance to immediately respawn at same location" },
  },

  // ── FORGE / SMELTING (8) ────────────────────────────────────────────────────
  {
    id: "thermal_efficiency", name: "Thermal Efficiency", category: "forge",
    sources: ["smelt"],
    rarity: "common", icon: "🔥",
    acquireDesc: "Complete 100 forge batches",
    stat: "smeltSpeed", statType: "forgeSpeed",
    statLabel: "Forge speed",
    ability1: { lv: 10, desc: "Every 20th batch completed produces 1 free bonus output" },
    ability2: { lv: 20, desc: "Forge speed bonus from this card also applies to bulk smelting operations" },
  },
  {
    id: "alloy_mastery", name: "Alloy Mastery", category: "forge",
    sources: ["smelt"],
    rarity: "common", icon: "⚙️",
    acquireDesc: "Smelt 200 Metal Alloy",
    stat: "metalAlloyOutput", statType: "resourceYield",
    statLabel: "Metal Alloy output",
    ability1: { lv: 10, desc: "Metal Alloy recipes cost 1 less Copper per batch (minimum 1)" },
    ability2: { lv: 20, desc: "Metal Alloy batches have 10% chance to produce double output" },
  },
  {
    id: "synth_weave", name: "Synth Weave", category: "forge",
    sources: ["smelt"],
    rarity: "uncommon", icon: "🧵",
    acquireDesc: "Smelt 200 Synthesized Alloy",
    stat: "synthAlloyOutput", statType: "resourceYield",
    statLabel: "Synth Alloy output",
    ability1: { lv: 8, desc: "Synthesized Alloy recipes cost 1 less Glass Shard per batch" },
    ability2: { lv: 18, desc: "Synth Alloy bonus also applies to all T1 components that use Synth Alloy" },
  },
  {
    id: "nano_forge", name: "Nano Forge", category: "forge",
    sources: ["smelt"],
    rarity: "rare", icon: "🔬",
    acquireDesc: "Smelt 100 Nano-Glass",
    stat: "nanoGlassOutput", statType: "resourceYield",
    statLabel: "Nano-Glass output",
    ability1: { lv: 6, desc: "Nano-Glass batches have 10% chance to also produce 1 free Optical Processor" },
    ability2: { lv: 15, desc: "Nano-Glass recipe costs 2 less Silica Dust per batch (minimum 1)" },
  },
  {
    id: "titanium_press", name: "Titanium Press", category: "forge",
    sources: ["smelt"],
    rarity: "rare", icon: "🔧",
    acquireDesc: "Smelt 100 Titanium Plates",
    stat: "titaniumPlatesOutput", statType: "resourceYield",
    statLabel: "Titanium Plates output",
    ability1: { lv: 6, desc: "Titanium Plates recipe costs 2 less Titanium Ore per batch" },
    ability2: { lv: 15, desc: "Titanium Plates batches have 8% chance to produce 1 free Kinetic Shielding" },
  },
  {
    id: "isotope_enricher", name: "Isotope Enricher", category: "forge",
    sources: ["smelt"],
    rarity: "rare", icon: "⚗️",
    acquireDesc: "Smelt 80 Enriched Cells",
    stat: "enrichedCellsOutput", statType: "resourceYield",
    statLabel: "Enriched Cells output",
    ability1: { lv: 6, desc: "Enriched Cells batches generate +4 bonus Echo Currency on completion" },
    ability2: { lv: 15, desc: "Enriched Cells recipe costs 1 less Isotope 8 per batch (minimum 1)" },
  },
  {
    id: "void_crucible", name: "Void Crucible", category: "forge",
    sources: ["smelt"],
    rarity: "epic", icon: "🫧",
    acquireDesc: "Smelt any T3 refined good 50 times",
    stat: "t3RefinedOutput", statType: "resourceYield",
    statLabel: "T3 refined output",
    ability1: { lv: 5, desc: "T3 refined batches have 5% chance to produce a free T3 Component of matching type" },
    ability2: { lv: 13, desc: "T3 refined recipes cost 10% fewer base resources at all times" },
  },
  {
    id: "quantum_synthesis", name: "Quantum Synthesis", category: "forge",
    sources: ["smelt"],
    rarity: "legendary", icon: "⚛️",
    acquireDesc: "Smelt a Quantum Core for the first time",
    stat: "allForgeOutput", statType: "forgeSpeed",
    statLabel: "ALL forge output",
    ability1: { lv: 3, desc: "The forge has a permanent 3% chance to complete any recipe instantly" },
    ability2: { lv: 10, desc: "Instant-complete chance increases to 8%, and triggers also produce double output" },
  },

  // ── COMBAT (12) ─────────────────────────────────────────────────────────────
  {
    id: "iron_shell", name: "Iron Shell", category: "combat",
    sources: ["combat"],
    rarity: "common", icon: "🛡️",
    acquireDesc: "Take 1,000 total damage",
    stat: "playerMaxHp", statType: "hp",
    statLabel: "Max HP",
    isFlat: true,
    ability1: { lv: 10, desc: "When HP drops below 20%, gain a 30 HP shield burst (60s cooldown)" },
    ability2: { lv: 20, desc: "Max HP bonus from this card is doubled while in T3 sectors" },
  },
  {
    id: "reactive_plating", name: "Reactive Plating", category: "combat",
    sources: ["combat"],
    rarity: "common", icon: "🔰",
    acquireDesc: "Block 500 damage with shields",
    stat: "shieldMax", statType: "shield",
    statLabel: "Shield capacity",
    isFlat: true,
    ability1: { lv: 10, desc: "Shield recharge begins 1 second sooner after last hit taken" },
    ability2: { lv: 20, desc: "When shield fully recharges, gain +8% damage for 5 seconds" },
  },
  {
    id: "quick_draw", name: "Quick Draw", category: "combat",
    sources: ["combat"],
    rarity: "common", icon: "⚡",
    acquireDesc: "Fire 2,000 projectiles total",
    stat: "blasterFireRate", statType: "speed",
    statLabel: "Attack speed",
    ability1: { lv: 10, desc: "Every 15th shot fires at double speed automatically" },
    ability2: { lv: 20, desc: "Attack speed bonus from this card also applies to mining laser rate" },
  },
  {
    id: "sharp_edge", name: "Sharp Edge", category: "combat",
    sources: ["combat"],
    rarity: "common", icon: "🗡️",
    acquireDesc: "Deal 5,000 total combat damage",
    stat: "blasterDamage", statType: "damage",
    statLabel: "Damage",
    ability1: { lv: 10, desc: "Hitting the same target twice within 0.5s deals +15% bonus on second hit" },
    ability2: { lv: 20, desc: "First projectile fired after a shield recharge always crits for ×2 damage" },
  },
  {
    id: "afterburner", name: "Afterburner", category: "combat",
    sources: ["combat"],
    rarity: "uncommon", icon: "🚀",
    acquireDesc: "Travel 500 sectors total",
    stat: "shipSpeed", statType: "speed",
    statLabel: "Movement speed",
    ability1: { lv: 8, desc: "Reaching max speed grants +10% damage for 3 seconds" },
    ability2: { lv: 18, desc: "Movement speed bonus also reduces fuel consumption by 1% per 5% speed gained" },
  },
  {
    id: "veterans_mark", name: "Veteran's Mark", category: "combat",
    sources: ["combat"],
    rarity: "uncommon", icon: "🎖️",
    acquireDesc: "Earn 10,000 total combat EXP",
    stat: "xpMult", statType: "exp",
    statLabel: "Combat EXP",
    ability1: { lv: 8, desc: "On kill, 10% chance the enemy drops a bonus Echo Currency pickup" },
    ability2: { lv: 18, desc: "Combat EXP bonus from this card also applies to EXP earned from rifts" },
  },
  {
    id: "kinetic_burst", name: "Kinetic Burst", category: "combat",
    sources: ["combat"],
    rarity: "rare", icon: "💥",
    acquireDesc: "Kill 200 enemies",
    stat: "blasterDamage", statType: "damage",
    statLabel: "Damage",
    ability1: { lv: 6, desc: "Every 10th kill triggers an area burst dealing 50 base damage to nearby enemies" },
    ability2: { lv: 15, desc: "Area burst damage scales with your current damage bonus (×1.5 multiplier)" },
  },
  {
    id: "void_piercer", name: "Void Piercer", category: "combat",
    sources: ["combat"],
    rarity: "rare", icon: "🌀",
    acquireDesc: "Kill 50 enemies inside rift zones",
    stat: "blasterDamage", statType: "damage",
    statLabel: "Damage in rifts",
    ability1: { lv: 6, desc: "Projectiles fired in rift zones pierce through 1 additional enemy" },
    ability2: { lv: 15, desc: "Rift zone kills have 15% chance to extend the rift timer by 3 seconds" },
  },
  {
    id: "neural_targeting", name: "Neural Targeting", category: "combat",
    sources: ["combat"],
    rarity: "epic", icon: "🧠",
    acquireDesc: "Land 500 consecutive hits without missing",
    stat: ["blasterFireRate", "blasterDamage"], statType: "speed",
    statLabel: "Attack speed + Damage",
    ability1: { lv: 5, desc: "After 10 consecutive hits, next shot deals triple damage" },
    ability2: { lv: 13, desc: "Consecutive hit counter resets only on miss, not on movement or weapon switching" },
  },
  {
    id: "phase_shield", name: "Phase Shield", category: "combat",
    sources: ["combat"],
    rarity: "epic", icon: "🔵",
    acquireDesc: "Fully recharge your shield 100 times",
    stat: ["shieldMax"], statType: "shield",
    statLabel: "Shield cap + Recharge rate",
    ability1: { lv: 5, desc: "When shield fully recharges, gain a 3-second window where shield cannot be depleted" },
    ability2: { lv: 13, desc: "Immunity window extends to 5 seconds and also grants +25% movement speed" },
  },
  {
    id: "star_breaker", name: "Star Breaker", category: "combat",
    sources: ["combat"],
    rarity: "epic", icon: "⭐",
    acquireDesc: "Kill 20 enemies using Star-Fire damage",
    stat: "blasterDamage", statType: "damage",
    statLabel: "Damage",
    ability1: { lv: 5, desc: "Star-Fire weapons ignite enemies — 5% of max HP burn damage over 3 seconds" },
    ability2: { lv: 13, desc: "Ignite spreads to 1 nearby enemy within range on application" },
  },
  {
    id: "apex_predator", name: "Apex Predator", category: "combat",
    sources: ["combat"],
    rarity: "legendary", icon: "🦅",
    acquireDesc: "Kill 1,000 total enemies",
    stat: ["blasterDamage", "blasterFireRate", "shipSpeed"], statType: "damage",
    statLabel: "Damage + Attack Speed + Move Speed",
    ability1: { lv: 3, desc: "On kill, gain 5-second buff: +20% damage, stacks up to 3×" },
    ability2: { lv: 10, desc: "Kill stacks no longer expire while actively firing; duration resets on each kill" },
  },

  // ── ECHO & RIFT (8) ─────────────────────────────────────────────────────────
  {
    id: "echo_pulse", name: "Echo Pulse", category: "echo",
    sources: ["any"],
    rarity: "common", icon: "〰️",
    acquireDesc: "Complete 20 echo events",
    stat: "echoCurrencyMult", statType: "echoCurrency",
    statLabel: "Echo Currency from all sources",
    ability1: { lv: 10, desc: "Echo events have 5% chance to drop a duplicate of the last card you leveled" },
    ability2: { lv: 20, desc: "Echo Currency pickups in the world are auto-collected from anywhere in the sector" },
  },
  {
    id: "rift_walker", name: "Rift Walker", category: "echo",
    sources: ["any"],
    rarity: "uncommon", icon: "🌌",
    acquireDesc: "Complete 30 rifts",
    stat: "riftEchoCurrency", statType: "echoCurrency",
    statLabel: "Echo Currency from rift events",
    ability1: { lv: 8, desc: "Rifts stay open 20% longer before collapsing" },
    ability2: { lv: 18, desc: "On rift completion, gain a free reroll token usable at the next shop visit" },
  },
  {
    id: "resonance_field", name: "Resonance Field", category: "echo",
    sources: ["any"],
    rarity: "rare", icon: "📡",
    acquireDesc: "Have 3+ Rift Echoes active simultaneously for 10 hours cumulative",
    stat: "activeCardBonus", statType: "echoCurrency",
    statLabel: "All active echo bonuses",
    ability1: { lv: 6, desc: "Each equipped card slot beyond the first amplifies all other active cards by +2%" },
    ability2: { lv: 15, desc: "The amplifier per extra slot increases to +4%" },
  },
  {
    id: "fragment_seeker", name: "Fragment Seeker", category: "echo",
    sources: ["any"],
    rarity: "rare", icon: "🔍",
    acquireDesc: "Collect 500 echo fragments total",
    stat: "echoFragmentQty", statType: "echoCurrency",
    statLabel: "Echo fragment quantity from rifts",
    ability1: { lv: 6, desc: "Echo fragments on the ground slowly drift toward your ship" },
    ability2: { lv: 15, desc: "Rift echo fragments count double toward card leveling costs" },
  },
  {
    id: "void_echo_card", name: "Void Echo", category: "echo",
    sources: ["any"],
    rarity: "epic", icon: "🕳️",
    acquireDesc: "Complete 20 void-tier rifts",
    stat: "voidRiftEchoCurrency", statType: "echoCurrency",
    statLabel: "Echo Currency from void rifts",
    ability1: { lv: 5, desc: "Void rifts have 10% chance to spawn a second echo event on completion" },
    ability2: { lv: 13, desc: "Second echo event chance increases to 25%, and it can be a different type" },
  },
  {
    id: "card_collector", name: "Card Collector", category: "echo",
    sources: ["any"],
    rarity: "epic", icon: "🃏",
    acquireDesc: "Own at least 25 unique cards",
    stat: "allCardBonusPerCards", statType: "echoCurrency",
    statLabel: "All card bonuses per 5 cards owned",
    ability1: { lv: 5, desc: "Every new unique card collected permanently increases all Echo Currency gain by +1%" },
    ability2: { lv: 13, desc: "New unique cards also permanently increase ALL resource yields by +0.5%" },
  },
  {
    id: "echo_cascade", name: "Echo Cascade", category: "echo",
    sources: ["any"],
    rarity: "epic", icon: "🌊",
    acquireDesc: "Trigger 3 echo events within 60 seconds, 10 separate times",
    stat: "echoCurrencyMult", statType: "echoCurrency",
    statLabel: "Echo Currency from all sources",
    ability1: { lv: 5, desc: "If 3+ echo events occur within 30 seconds, next card level-up costs 20% fewer echoes" },
    ability2: { lv: 13, desc: "Cost reduction increases to 35% and can trigger once per minute" },
  },
  {
    id: "singularity_card", name: "Singularity Card", category: "echo",
    sources: ["any"],
    rarity: "legendary", icon: "💫",
    acquireDesc: "Reach Level 10 on any 5 different cards",
    stat: "allEchoCardBonus", statType: "echoCurrency",
    statLabel: "All echo card bonuses",
    ability1: { lv: 3, desc: "Each maxed card (Level 20) you own permanently increases all Echo Currency gain by +4%" },
    ability2: { lv: 10, desc: "Maxed cards also each contribute +1% to ALL gameplay stats permanently" },
  },

  // ── TRADE (6) ───────────────────────────────────────────────────────────────
  {
    id: "market_sense", name: "Market Sense", category: "trade",
    sources: ["any"],
    rarity: "common", icon: "📈",
    acquireDesc: "Complete 50 trades",
    stat: "tradeSellBonus", statType: "resourceYield",
    statLabel: "Sell value",
    ability1: { lv: 10, desc: "Selling T1 refined goods has 5% chance to sell at double value" },
    ability2: { lv: 20, desc: "Double-value chance applies to all tiers and increases to 8%" },
  },
  {
    id: "bulk_dealer", name: "Bulk Dealer", category: "trade",
    sources: ["any"],
    rarity: "uncommon", icon: "📦",
    acquireDesc: "Sell 10,000 units of any resource",
    stat: "tradeSellBonus", statType: "resourceYield",
    statLabel: "Bulk sell value",
    ability1: { lv: 8, desc: "Bulk sales of T2+ goods have 8% chance to generate a bonus Echo Currency drop" },
    ability2: { lv: 18, desc: "Bulk threshold for bonus reduces from 10 units to 5 units" },
  },
  {
    id: "supply_chain", name: "Supply Chain", category: "trade",
    sources: ["any"],
    rarity: "rare", icon: "🔗",
    acquireDesc: "Buy 500 items from shops",
    stat: "tradeBuyMult", statType: "resourceYield",
    statLabel: "Buy price reduction",
    ability1: { lv: 6, desc: "Shop stock quantities increase by +2 across all listed items" },
    ability2: { lv: 15, desc: "Shop restocks 15% faster after purchase" },
  },
  {
    id: "void_broker", name: "Void Broker", category: "trade",
    sources: ["any"],
    rarity: "epic", icon: "💎",
    acquireDesc: "Sell 100 T3 refined goods",
    stat: "tradeSellBonus", statType: "resourceYield",
    statLabel: "T3 sell value",
    ability1: { lv: 5, desc: "Selling T3 goods at the Trade Beacon generates a Rift Echo event" },
    ability2: { lv: 13, desc: "Rift Echo from sales is guaranteed to drop Echo Currency at maximum roll" },
  },
  {
    id: "galactic_merchant", name: "Galactic Merchant", category: "trade",
    sources: ["any"],
    rarity: "epic", icon: "🌐",
    acquireDesc: "Use the Trade Beacon 200 times",
    stat: ["tradeSellBonus", "tradeBuyMult"], statType: "resourceYield",
    statLabel: "Sell value + Buy price",
    ability1: { lv: 5, desc: "Trade Beacon restocks 25% faster" },
    ability2: { lv: 13, desc: "Trade Beacon always matches the single best price available across all markets" },
  },
  {
    id: "monopoly_engine", name: "Monopoly Engine", category: "trade",
    sources: ["any"],
    rarity: "legendary", icon: "🏦",
    acquireDesc: "Earn 1,000,000 credits total from trading",
    stat: "tradeSellBonus", statType: "resourceYield",
    statLabel: "Sell value on ALL goods",
    ability1: { lv: 3, desc: "Once per shop visit, one random item is offered at cost price (effectively free)" },
    ability2: { lv: 10, desc: "Free item per visit upgrades to one free item of your choice per visit" },
  },

  // ── EXPLORATION (6) ─────────────────────────────────────────────────────────
  {
    id: "scouts_eye", name: "Scout's Eye", category: "exploration",
    sources: ["any"],
    rarity: "common", icon: "👁️",
    acquireDesc: "Discover 20 new sectors",
    stat: "scannerReveal", statType: "resourceYield",
    statLabel: "Node detection radius",
    ability1: { lv: 10, desc: "Newly discovered sectors reveal all resource nodes immediately on enter" },
    ability2: { lv: 20, desc: "Detection radius bonus also reveals hidden echo event locations" },
  },
  {
    id: "fuel_cell", name: "Fuel Cell", category: "exploration",
    sources: ["any"],
    rarity: "common", icon: "⛽",
    acquireDesc: "Consume 1,000 total fuel units",
    stat: "fuelEfficiency", statType: "fuelEfficiency",
    statLabel: "Fuel efficiency",
    ability1: { lv: 10, desc: "Fuel cost is waived entirely for the first move after docking" },
    ability2: { lv: 20, desc: "Every 10 sectors traveled without docking restores 5 fuel units" },
  },
  {
    id: "deep_space_nav", name: "Deep Space Navigator", category: "exploration",
    sources: ["any"],
    rarity: "uncommon", icon: "🧭",
    acquireDesc: "Enter a T3 sector for the first time",
    stat: "fuelEfficiency", statType: "fuelEfficiency",
    statLabel: "Fuel efficiency in T3 sectors",
    ability1: { lv: 8, desc: "T3 sector nodes yield +10% on first harvest per visit" },
    ability2: { lv: 18, desc: "T3 sector nodes have +5% chance to drop a T3 component on depletion" },
  },
  {
    id: "wreck_hunter", name: "Wreck Hunter", category: "exploration",
    sources: ["any"],
    rarity: "rare", icon: "🏴‍☠️",
    acquireDesc: "Loot 100 wreck fields",
    stat: "bonusLoot", statType: "resourceYield",
    statLabel: "Loot lifetime in the world",
    ability1: { lv: 6, desc: "Wreck fields have 10% chance to contain a random T2 component" },
    ability2: { lv: 15, desc: "T2 component chance increases to 20%, and T3 components can appear at 3%" },
  },
  {
    id: "horizon_breaker", name: "Horizon Breaker", category: "exploration",
    sources: ["any"],
    rarity: "epic", icon: "🌅",
    acquireDesc: "Discover all sectors in a full sector ring",
    stat: ["fuelEfficiency", "scannerReveal"], statType: "fuelEfficiency",
    statLabel: "Fuel efficiency + Detection radius",
    ability1: { lv: 5, desc: "Once per sector, detecting a resource node also reveals a nearby hidden echo event" },
    ability2: { lv: 13, desc: "Hidden echo event reveal now shows the event type and estimated echo currency value" },
  },
  {
    id: "void_atlas", name: "Void Atlas", category: "exploration",
    sources: ["any"],
    rarity: "legendary", icon: "🌍",
    acquireDesc: "Discover every sector type (all 6 biomes) AND complete a rift in each biome",
    stat: ["allResourceYield", "fuelEfficiency", "scannerReveal"], statType: "resourceYield",
    statLabel: "All resources + Fuel + Detection",
    ability1: { lv: 3, desc: "All previously visited sectors show live resource node status on the map at all times" },
    ability2: { lv: 10, desc: "Visiting a sector for the 5th+ time grants permanent +2% yield to all nodes there, stacking" },
  },
];

// Quick lookup by id
export const ECHO_CARD_MAP = Object.fromEntries(ECHO_CARDS.map(c => [c.id, c]));

// Rarity drop pool weights for random card drops
export const CARD_DROP_WEIGHTS = { common: 50, uncommon: 25, rare: 15, epic: 8, legendary: 2 };

// Cards grouped by rarity for easy sampling
const BY_RARITY = {};
for (const c of ECHO_CARDS) {
  if (!BY_RARITY[c.rarity]) BY_RARITY[c.rarity] = [];
  BY_RARITY[c.rarity].push(c.id);
}
export const CARDS_BY_RARITY = BY_RARITY;

// Cards grouped by source — keys are material IDs, "combat", "smelt", or "any"
// "any" cards are included in every pool
const BY_SOURCE = {};
for (const c of ECHO_CARDS) {
  for (const src of (c.sources ?? ["any"])) {
    if (!BY_SOURCE[src]) BY_SOURCE[src] = [];
    BY_SOURCE[src].push(c.id);
  }
}
export const CARDS_BY_SOURCE = BY_SOURCE;
